import React, { useEffect, useState, useContext } from "react";
import { makeStyles } from "@material-ui/core/styles";
import { toast } from "react-toastify";
import { Palette, Apps } from "@material-ui/icons";
import { Paper, Typography, Grid, TextField, IconButton, InputAdornment } from "@material-ui/core";
import ColorBoxModal from "../ColorBoxModal/index.js";
import ColorModeContext from "../../layout/themeContext";
import api from "../../services/api";
import { i18n } from "../../translate/i18n";

const useStyles = makeStyles((theme) => ({
  container: {
    paddingTop: theme.spacing(2),
    paddingBottom: theme.spacing(4),
  },
  paper: {
    padding: theme.spacing(3),
    marginBottom: theme.spacing(3),
    borderRadius: theme.spacing(1),
  },
  sectionHeader: {
    display: "flex",
    alignItems: "center",
    marginBottom: theme.spacing(2),
  },
  sectionIcon: {
    marginRight: theme.spacing(1),
    color: theme.palette.primary.main,
  },
  sectionTitle: {
    fontWeight: 600,
  },
  formField: {
    marginBottom: theme.spacing(2),
  },
  colorAdornment: {
    width: 20,
    height: 20,
    borderRadius: 4,
    border: "1px solid #ddd",
  },
}));

export default function Whitelabel(props) {
  const { settings } = props;
  const classes = useStyles();
  const { colorMode } = useContext(ColorModeContext);
  
  const [appName, setAppName] = useState("");
  const [primaryColorLight, setPrimaryColorLight] = useState("#1976d2");
  const [primaryColorDark, setPrimaryColorDark] = useState("#1976d2");
  const [listItemBgColor, setListItemBgColor] = useState("#ffffff");
  
  const [colorLightOpen, setColorLightOpen] = useState(false);
  const [colorDarkOpen, setColorDarkOpen] = useState(false);
  const [colorBgOpen, setColorBgOpen] = useState(false);
  
  const [loading, setLoading] = useState(true);

  // Carregar configurações
  useEffect(() => {
    const loadSettings = async () => {
      try {
        const { data } = await api.get("/settings");
        if (Array.isArray(data) && data.length > 0) {
          const appNameSetting = data.find((s) => s?.key === "appName");
          const colorLightSetting = data.find((s) => s?.key === "primaryColorLight");
          const colorDarkSetting = data.find((s) => s?.key === "primaryColorDark");
          const colorBgSetting = data.find((s) => s?.key === "listItemBgColor");

          if (appNameSetting?.value) setAppName(appNameSetting.value);
          if (colorLightSetting?.value) setPrimaryColorLight(colorLightSetting.value);
          if (colorDarkSetting?.value) setPrimaryColorDark(colorDarkSetting.value);
          if (colorBgSetting?.value) setListItemBgColor(colorBgSetting.value);
        }
      } catch (error) {
        console.error("Erro ao carregar configurações:", error);
        toast.error("Erro ao carregar configurações");
      } finally {
        setLoading(false);
      }
    };

    // Se settings foi passado como prop, usar ele
    if (settings && Array.isArray(settings) && settings.length > 0) {
      const appNameSetting = settings.find((s) => s?.key === "appName");
      const colorLightSetting = settings.find((s) => s?.key === "primaryColorLight");
      const colorDarkSetting = settings.find((s) => s?.key === "primaryColorDark");
      const colorBgSetting = settings.find((s) => s?.key === "listItemBgColor");

      if (appNameSetting?.value) setAppName(appNameSetting.value);
      if (colorLightSetting?.value) setPrimaryColorLight(colorLightSetting.value);
      if (colorDarkSetting?.value) setPrimaryColorDark(colorDarkSetting.value);
      if (colorBgSetting?.value) setListItemBgColor(colorBgSetting.value);
      setLoading(false);
    } else {
      // Caso contrário, buscar do backend
      loadSettings();
    }
  }, [settings]);

  const saveSetting = async (key, value) => {
    try {
      console.log("Salvando:", { key, value });
      const response = await api.put(`/settings/${key}`, { value });
      console.log("Resposta:", response);
      toast.success("✅ Salvo com sucesso!");
      return true;
    } catch (error) {
      console.error("Erro completo:", error);
      console.error("Response data:", error.response?.data);
      console.error("Response status:", error.response?.status);
      const errorMsg = error.response?.data?.error || error.response?.data?.message || error.message;
      toast.error("❌ Erro ao salvar: " + errorMsg);
      return false;
    }
  };

  const handleSaveAppName = async () => {
    if (!appName.trim()) {
      toast.error("Nome não pode estar vazio");
      return;
    }
    const success = await saveSetting("appName", appName);
    if (success) {
      colorMode.setAppName(appName);
    }
  };

  const handleColorChange = async (key, color, setter) => {
    let hexColor = color;
    if (typeof color === "object" && color.hex) {
      hexColor = `#${color.hex}`;
    } else if (typeof color === "string" && !color.startsWith("#")) {
      hexColor = `#${color}`;
    }

    setter(hexColor);
    const success = await saveSetting(key, hexColor);
    
    if (success) {
      if (key === "primaryColorLight") {
        colorMode.setPrimaryColorLight(hexColor);
      } else if (key === "primaryColorDark") {
        colorMode.setPrimaryColorDark(hexColor);
      } else if (key === "listItemBgColor") {
        colorMode.setListItemBgColor(hexColor);
      }
    }
  };

  if (loading) {
    return <div style={{ padding: "50px", textAlign: "center" }}>Carregando...</div>;
  }

  return (
    <div className={classes.container}>
      {/* Seção de Configurações Gerais */}
      <Paper className={classes.paper}>
        <div className={classes.sectionHeader}>
          <Apps className={classes.sectionIcon} />
          <Typography variant="h6" className={classes.sectionTitle}>
            {i18n.t("whitelabel.sections.general")}
          </Typography>
        </div>

        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label={i18n.t("whitelabel.appName")}
              value={appName}
              onChange={(e) => setAppName(e.target.value)}
              onBlur={handleSaveAppName}
              variant="outlined"
              size="small"
            />
          </Grid>
        </Grid>
      </Paper>

      {/* Seção de Cores */}
      <Paper className={classes.paper}>
        <div className={classes.sectionHeader}>
          <Palette className={classes.sectionIcon} />
          <Typography variant="h6" className={classes.sectionTitle}>
            {i18n.t("whitelabel.sections.colors")}
          </Typography>
        </div>

        <Grid container spacing={2}>
          {/* Cor Primária Tema Claro */}
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label={i18n.t("whitelabel.primaryColorLight")}
              value={primaryColorLight}
              onClick={() => setColorLightOpen(true)}
              variant="outlined"
              size="small"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <div
                      style={{ backgroundColor: primaryColorLight }}
                      className={classes.colorAdornment}
                    />
                  </InputAdornment>
                ),
              }}
              readOnly
            />
            <ColorBoxModal
              open={colorLightOpen}
              handleClose={() => setColorLightOpen(false)}
              onChange={(color) => {
                handleColorChange("primaryColorLight", color, setPrimaryColorLight);
                setColorLightOpen(false);
              }}
              currentColor={primaryColorLight}
            />
          </Grid>

          {/* Cor Primária Tema Escuro */}
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label={i18n.t("whitelabel.primaryColorDark")}
              value={primaryColorDark}
              onClick={() => setColorDarkOpen(true)}
              variant="outlined"
              size="small"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <div
                      style={{ backgroundColor: primaryColorDark }}
                      className={classes.colorAdornment}
                    />
                  </InputAdornment>
                ),
              }}
              readOnly
            />
            <ColorBoxModal
              open={colorDarkOpen}
              handleClose={() => setColorDarkOpen(false)}
              onChange={(color) => {
                handleColorChange("primaryColorDark", color, setPrimaryColorDark);
                setColorDarkOpen(false);
              }}
              currentColor={primaryColorDark}
            />
          </Grid>

          {/* Cor de Fundo dos Itens */}
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label={i18n.t("whitelabel.listItemBgColor")}
              value={listItemBgColor}
              onClick={() => setColorBgOpen(true)}
              variant="outlined"
              size="small"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <div
                      style={{ backgroundColor: listItemBgColor }}
                      className={classes.colorAdornment}
                    />
                  </InputAdornment>
                ),
              }}
              readOnly
            />
            <ColorBoxModal
              open={colorBgOpen}
              handleClose={() => setColorBgOpen(false)}
              onChange={(color) => {
                handleColorChange("listItemBgColor", color, setListItemBgColor);
                setColorBgOpen(false);
              }}
              currentColor={listItemBgColor}
            />
          </Grid>
        </Grid>
      </Paper>
    </div>
  );
}
